import { create } from 'zustand';
import { FEATURES_REGISTRY, type FeatureId } from '@/data/featuresRegistry';
import { apiFetch } from '@/services/api/client';

type FeatureState = {
  flags: Record<FeatureId, boolean>;
  hydrated: boolean;
  isOn: (id: FeatureId) => boolean;
  hydrate: () => Promise<void>;
  set: (id: FeatureId, on: boolean) => Promise<void>;
  toggle: (id: FeatureId) => Promise<void>;
  enabledCount: () => number;
};

const defaults = (): Record<FeatureId, boolean> =>
  Object.fromEntries(FEATURES_REGISTRY.map((feature) => [feature.id, !feature.defaultOff])) as Record<FeatureId, boolean>;

export const useFeaturesStore = create<FeatureState>((set, get) => ({
  flags: defaults(),
  hydrated: false,
  isOn: (id) => get().flags[id] === true,
  hydrate: async () => {
    try {
      const response = await apiFetch<{ flags: Partial<Record<FeatureId, boolean>> }>('/feature_flags');
      set({ flags: { ...defaults(), ...response.flags }, hydrated: true });
    } catch {
      set({ hydrated: true });
    }
  },
  set: async (id, on) => {
    await apiFetch(`/admin/feature_flags/${id}`, { method: 'PATCH', body: { enabled: on } });
    set({ flags: { ...get().flags, [id]: on } });
  },
  toggle: async (id) => get().set(id, !get().flags[id]),
  enabledCount: () => Object.values(get().flags).filter(Boolean).length,
}));

export const useFeature = (id: FeatureId): boolean => useFeaturesStore((state) => state.flags[id] === true);
